import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SnackbarService } from '../../../services/design/snackbar.service';
import { Rider, RiderService } from '../../../services/riders/rider.service';
import {
  AdminRiderWalletDetails,
  RiderWalletAdminService,
  WalletAdjustAction,
} from '../../../services/riders/rider-wallet-admin.service';
import { StoreOption, StoreService } from '../../../services/stores/store.service';
import { AdminPaginatorComponent } from '../../../Design/admin-paginator.component';
import { AuthService } from '../../../services/auth/auth.service';

@Component({
  selector: 'app-rider-wallet-admin',
  standalone: true,
  imports: [CommonModule, FormsModule, AdminPaginatorComponent],
  templateUrl: './rider-wallet-admin.component.html',
})
export class RiderWalletAdminComponent implements OnInit {
  readonly auth=inject(AuthService);
  private riderService = inject(RiderService);
  private walletService = inject(RiderWalletAdminService);
  private storeService = inject(StoreService);
  private snackbar = inject(SnackbarService);

  riders = signal<Rider[]>([]);
  stores = signal<StoreOption[]>([]);
  details = signal<AdminRiderWalletDetails | null>(null);

  selectedStoreId = 0;
  selectedRiderId = '';
  search = '';
  riderPage = 1;
  riderPageSize = 10;
  isLoading = signal(false);
  isSaving = signal(false);

  action: WalletAdjustAction = 'credit';
  amount = '';
  reason = '';
  adjustLifetime = false;
  setCodLiability = false;
  codLiability = '';
  private adjustmentPayload = '';
  private adjustmentRequest = '';

  ngOnInit(): void {
    this.selectedStoreId=this.auth.isSuperAdmin()?0:Number(this.auth.storeId());
    this.storeService.allStores().subscribe({
      next: (stores) => this.stores.set(stores.filter(s=>this.auth.isSuperAdmin()||Number(s.id)===this.selectedStoreId)),
    });
    this.loadRiders();
  }

  loadRiders() {
    this.riderService.getFinanceRiders().subscribe({
      next: (riders) => this.riders.set(riders),
      error: () => this.snackbar.error('Failed to load riders'),
    });
  }

  filteredRiders(): Rider[] {
    const q = this.search.trim().toLowerCase();
    return this.riders().filter((rider) => {
      if (this.selectedStoreId > 0 && Number(rider.store_id) !== this.selectedStoreId) {
        return false;
      }
      if (!q) return true;
      return (
        rider.full_name?.toLowerCase().includes(q) ||
        rider.code?.toLowerCase().includes(q) ||
        rider.id?.toLowerCase().includes(q)
      );
    });
  }

  paginatedRiders(): Rider[] {
    const start = (this.riderPage - 1) * this.riderPageSize;
    return this.filteredRiders().slice(start, start + this.riderPageSize);
  }

  resetRiderPage(): void {
    this.riderPage = 1;
  }

  changeRiderPageSize(size: number): void {
    this.riderPageSize = size;
    this.riderPage = 1;
  }

  selectRider(riderId: string) {
    this.selectedRiderId = riderId;
    this.resetForm();
    this.loadWallet();
  }

  loadWallet() {
    if (!this.selectedRiderId) return;
    this.isLoading.set(true);
    this.walletService.getWallet(this.selectedRiderId).subscribe({
      next: (data) => {
        this.details.set(data);
        this.codLiability = String(data.wallet.cod_liability ?? 0);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.snackbar.error(err?.message ?? 'Failed to load wallet');
        this.isLoading.set(false);
      },
    });
  }

  resetForm() {
    this.adjustmentPayload = '';
    this.adjustmentRequest = '';
    this.action = 'credit';
    this.amount = '';
    this.reason = '';
    this.adjustLifetime = false;
    this.setCodLiability = false;
  }

  submitAdjustment() {
    if(this.isSaving())return;
    const amount = Number(this.amount);
    if (!this.selectedRiderId) {
      this.snackbar.warning('Select a rider first');
      return;
    }
    if (!this.reason.trim()) {
      this.snackbar.warning('Reason is required');
      return;
    }
    if (!Number.isFinite(amount) || (this.action !== 'set' && amount <= 0)) {
      this.snackbar.warning('Enter a valid amount');
      return;
    }
    if (this.action === 'set' && amount < 0) {
      this.snackbar.warning('Balance cannot be negative');
      return;
    }

    const label =
      this.action === 'credit'
        ? `credit ₹${amount}`
        : this.action === 'debit'
          ? `debit ₹${amount}`
          : `set balance to ₹${amount}`;

    if (!confirm(`Confirm ${label} for this rider?`)) return;

    const payload=JSON.stringify([this.selectedRiderId,this.action,amount,this.reason.trim(),this.adjustLifetime,this.setCodLiability?Number(this.codLiability||0):null]);
    if(payload!==this.adjustmentPayload){this.adjustmentPayload=payload;this.adjustmentRequest=crypto.randomUUID();}

    this.isSaving.set(true);
    this.walletService
      .adjust({
        requestId: this.adjustmentRequest,
        riderId: this.selectedRiderId,
        action: this.action,
        amount,
        reason: this.reason.trim(),
        adjustLifetime: this.adjustLifetime,
        adjustCodLiability: this.setCodLiability
          ? Number(this.codLiability || 0)
          : null,
      })
      .subscribe({
        next: (res) => {
          this.snackbar.success(
            `Wallet updated: ₹${res.balance_before} → ₹${res.balance_after}`
          );
          this.resetForm();
          this.loadWallet();
          this.loadRiders();
          this.isSaving.set(false);
        },
        error: (err) => {
          this.snackbar.error(err?.message ?? 'Wallet adjustment failed');
          this.isSaving.set(false);
        },
      });
  }
}
